import React from "react";
import { EventManager } from "./EventManager";
/** Given an array of classNames or a single className, creates a single className with the default passed in
 * @param classes The class(es) to concat
 * @param classname The base className to join
 * @returns string, the joined classNames
 */
export var concatClassNames = function (classes, className) {
    var isArray = Array.isArray(classes);
    if (!className)
        return isArray ? classes.filter(function (f) { return !!f; }).join(" ").trim() : classes;
    if (isArray)
        return classes.filter(function (c) { return !!c; }).concat(className).join(" ").trim();
    return (classes + " " + className).trim();
};
/** Given a specific component, filters the children by the passed class
 * @param that The class with the children to be filtered
 * @param type The type of class which the children should respect
 * @returns The filtered children
 */
export var filterChildrenOfType = function (that, type) {
    var arr = React.Children.map(that.props.children, function (c) { return c; }).map(function (c) { return c; });
    return arr.filter(function (a) { return a.type === type; });
};
/** Given a specific component, filters the children and excludes the ones with the passed class
 * @param that The class with the children to be filtered
 * @param type The type of class which the children should NOT respect
 * @returns The filtered children
 */
export var excludeChildrenOfType = function (that, type) {
    var arr = React.Children.map(that.props.children, function (c) { return c; }).map(function (c) { return c; });
    return arr.filter(function (a) { return a.type !== type; });
};
/** Creates a document element with a base className
 * @param type The type of element to be created, based on JavaScript
 * @param className The className to add
 * @returns The created document element
 */
export var createElementWithClass = function (type, className) {
    var elem = document.createElement(type);
    elem.classList.add(className);
    return elem;
};
/** Adds a function to be triggered when the user clicks outside the component. Should be called in componentDidMount
 * @param fn The function to trigger when the user clicks outside
 * @param that The React component instance to be checked
 * @param excludeElement Eventual element to be excluded when checking
 */
export var addCheckClickOutside = function (fn, that, excludeElement) {
    var internalFunction = function (e) {
        var _a;
        var target = e.target;
        if (!((_a = that.node) === null || _a === void 0 ? void 0 : _a.contains(target)) && (!excludeElement || !excludeElement.contains(target)) && fn)
            fn(e);
    };
    editComponentUnmount(internalFunction, that);
};
/** Edits the componentWillUnmount function of the component adding a window listener. Should be called in componentDidMount
 * @param fn The function to triggered by the listener
 * @param that The React component instance to be checked
 * @param event The type of event to check (default is "click")
 */
export var editComponentUnmount = function (fn, that, event) {
    if (event === void 0) { event = "click"; }
    var componentWillUnmount = that.componentWillUnmount, ev = new EventManager(event, fn).register();
    that.componentWillUnmount = function () {
        if (componentWillUnmount)
            componentWillUnmount();
        ev.unregister();
    };
};
/** Function used to track an element's position when the user scrolls the page, automatically adjusting the position of the element inside the page
 * @param element The element to track
 * @param that The React component instance that hosts the element
 * @param useWidth Determines wether the width of the component node should be considered in the calculation
 * @param centerize Determines wether the element should be positioned in the center
 */
export var trackOnElement = function (element, that, useWidth, centerize, ignoreElementSelector) {
    if (useWidth === void 0) { useWidth = true; }
    if (centerize === void 0) { centerize = true; }
    var reattachElement = function () {
        var _a = that.node.getBoundingClientRect(), top = _a.top, left = _a.left, width = _a.width, height = _a.height, baseZIndex = Number(getComputedStyle(element).getPropertyValue("z-index")), ignored = that.node.querySelector(ignoreElementSelector);
        var parent = that.node, zIndex = isNaN(baseZIndex) ? 0 : baseZIndex, useTop = ignored ? top + ignored.getBoundingClientRect().height : top;
        element.style.top = top + height + "px";
        element.style.left = left + "px";
        if (useWidth) {
            if (centerize)
                element.style.width = width + "px";
        }
        else {
            var eleWidth = element.getBoundingClientRect().width;
            if (width < eleWidth)
                element.style.left = left - ((eleWidth - width) / 2) + "px";
            else
                element.style.left = left + ((width - eleWidth) / 2) + "px";
        }
        element.classList.toggle("on-top", !isElementInViewport(element));
        that.node.classList.toggle("on-top", !isElementInViewport(element));
        if (!isElementInViewport(element))
            element.style.top = "calc(".concat(useTop - element.offsetHeight, "px - ").concat(window.getComputedStyle(element).marginBottom, ")");
        if (!isElementInViewport(element))
            element.style.top = useTop + height + "px";
        while (parent) {
            var elementZIndex = getComputedStyle(parent).getPropertyValue("z-index");
            if (!isNaN(Number(elementZIndex)))
                zIndex += Number(elementZIndex);
            parent = parent.parentElement;
        }
        element.style.zIndex = zIndex.toString();
    };
    setTimeout(reattachElement);
    editComponentUnmount(reattachElement, that, "scroll");
    editComponentUnmount(reattachElement, that, "resize");
};
/** Determines wether the element passed is inside the viewport or is cut out
 * @param element The element to check
 * @returns boolean
 */
export var isElementInViewport = function (element) {
    var rect = element.getBoundingClientRect();
    return rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
};
/** Creates an array of number from the starting to the ending
 * @param start The start number
 * @param end The end number
 * @returns number[]
 */
export var range = function (start, stop) {
    return Array.from({ length: stop - start }, function (_, i) { return start + i; });
};
/** Function to determine if an element is showing the scrollbar
 * @param element The element to check
 * @returns boolean
 */
export var isElementOverflowing = function (element) {
    return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
};
/** Function to check if one of the keys has been pressed by the user, and triggers a function
 * @param e The keyboard event
 * @param fn The function to be triggered
 * @param check Additional check: if true, the function won't be triggered
 * @param keys The keys to check. By default Space and Enter
 */
export var checkKey = function (e, fn, check, keys) {
    if (check === void 0) { check = false; }
    if (keys === void 0) { keys = ["Space", "Enter"]; }
    if (check)
        return;
    if (keys.includes(e.code))
        fn();
};
/** Function to apply react CSS style or classes to HTML element
 * @param e The HTML element
 * @param style The CSSProperties
 * @param className Eventual className to add (will be splitted to Space char)
 */
export var applyStyleOrClass = function (e, style, className) {
    if (style && e)
        Object.keys(style).forEach(function (k) { return e.style[k] = style[k]; });
    if (className)
        className.split(" ").forEach(function (c) { return e.classList.add(c); });
};
/** Given a month and a year, returns the last day of that month, considering leap years
 * @param month The month with 0 index
 * @param year The year
 * @returns number
 */
export var getLastDayOfMonth = function (month, year) {
    switch (month) {
        case 0:
        case 2:
        case 4:
        case 6:
        case 7:
        case 9:
        case 11:
            return 31;
        case 1:
            if (year % 100 === 0 || year % 4 === 0 || year % 400 === 0)
                return 29;
            return 28;
        default:
            return 30;
    }
};
/** Given a year and a month, returns an array of weeks containing numbers (if the days are in the current month) or strings as follows:
 * @_number If the day is in the previous month
 * @number_ If the day is in the next month
 * @param year The year of the calendar
 * @param month The month to get
 * @param getAfterAndBefore If true, checks the days after and before the month to build a 6 columns calendar
 * @returns Array of numbers or strings
 */
export var getCalendar = function (year, month, getAfterAndBefore) {
    if (getAfterAndBefore === void 0) { getAfterAndBefore = true; }
    var date = new Date(year, month, 1), rows = [], lastDay = getLastDayOfMonth(month, year), emptySpaces = date.getDay() === 0 ? 6 : date.getDay() - 1;
    var row = range(0, emptySpaces).map(function () { return 0; }), append = [], secondRow = [];
    if (getAfterAndBefore) {
        var getMonthBefore = new Date(date), getMonthAfter = new Date(date);
        getMonthAfter.setMonth(getMonthAfter.getMonth() + 1);
        getMonthBefore.setMonth(getMonthBefore.getMonth() - 1);
        var calendarBefore = getCalendar(getMonthBefore.getFullYear(), getMonthBefore.getMonth(), false), calendarAfter = getCalendar(getMonthAfter.getFullYear(), getMonthAfter.getMonth(), false);
        if (emptySpaces > 0)
            row = calendarBefore[calendarBefore.length - 1].filter(function (c) { return Number(c) > 0; }).map(function (d) { return "_" + d; });
        append = calendarAfter[0].filter(function (c) { return Number(c) > 0; }).map(function (c) { return c + "_"; });
        secondRow = calendarAfter[1].map(function (c) { return c + "_"; });
    }
    if (row.length === 7)
        row = [];
    for (var i = 1; i <= lastDay; i++) {
        if (row.length === 7) {
            rows.push(row);
            row = [];
        }
        row.push(i);
        if (i === lastDay) {
            var daysAfter = 7 - row.length;
            rows.push(row.concat(append.slice(0, daysAfter)));
            if (rows.length < 6 && secondRow.length > 0)
                rows.push(secondRow);
        }
    }
    return rows;
};
